/**
 * PauseTaskModal - 暂停任务确认 Modal
 *
 * 用于暂停正在进行中的任务：
 * - 暂停原因（必选，下拉选择 + Other 自定义）
 * - 备注（可选）
 */

import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Select } from 'antd'
import {
  X,
  Pause,
  FileText,
} from 'lucide-react'
import { Button } from '../../ui/button'
import { Input } from '../../ui/input'
import { Label } from '../../ui/label'
import { Textarea } from '../../ui/textarea'

const PAUSE_REASONS = [
  'Waiting for appointment',
  'Driver break / rest',
  'Mechanical issue',
  'Weather delay',
  'Port congestion',
  'Waiting for chassis',
  'Waiting for container release',
  'Customer not ready',
  'Documentation issue',
  'Traffic delay',
] as const

export interface PauseTaskData {
  reason: string
  note?: string
}

interface PauseTaskModalProps {
  open: boolean
  onClose: () => void
  onConfirm: (data: PauseTaskData) => Promise<void>
  isSaving?: boolean
  taskId: string
  containerNumber?: string
}

export const PauseTaskModal: React.FC<PauseTaskModalProps> = ({
  open,
  onClose,
  onConfirm,
  isSaving = false,
  taskId,
  containerNumber
}) => {
  const [selectedReason, setSelectedReason] = useState('')
  const [customReason, setCustomReason] = useState('')
  const [note, setNote] = useState('')

  const isOther = selectedReason === '__other__'
  const finalReason = isOther ? customReason.trim() : selectedReason
  const canSubmit = finalReason.length > 0

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setSelectedReason('')
      setCustomReason('')
      setNote('')
    }
  }, [open])

  const handleConfirm = async () => {
    if (!canSubmit) return
    await onConfirm({
      reason: finalReason,
      note: note.trim() || undefined
    })
  }

  if (!open) return null

  // z-[100]：必须高过详情弹窗的 z-[70]，否则被它的黑底盖住＝点了没反应
  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-orange-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Pause className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Pause Task</h3>
              <p className="text-sm text-gray-500">
                {containerNumber ? `Container: ${containerNumber}` : `Task: ${taskId}`}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-gray-600">
            Pausing this task will stop the current operation. You can resume it later or reschedule/split if needed.
          </p>

          {/* Reason Select */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-gray-400" />
              Pause Reason <span className="text-red-500">*</span>
            </Label>
            <Select
              value={selectedReason || undefined}
              onChange={(v) => {
                setSelectedReason(v)
                if (v !== '__other__') setCustomReason('')
              }}
              placeholder="Select a reason..."
              style={{ width: '100%' }}
              getPopupContainer={(trigger) => trigger.closest('[role="dialog"]') as HTMLElement || document.body}
            >
              {PAUSE_REASONS.map((r) => (
                <Select.Option key={r} value={r}>{r}</Select.Option>
              ))}
              <Select.Option value="__other__">Other (custom reason)</Select.Option>
            </Select>
          </div>

          {/* Custom Reason Input (shown when "Other" selected) */}
          {isOther && (
            <div className="space-y-2">
              <Label className="text-sm text-gray-600">
                Custom Reason <span className="text-red-500">*</span>
              </Label>
              <Input
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                placeholder="Enter your reason..."
                autoFocus
              />
            </div>
          )}

          {/* Note */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-gray-400" />
              Note (Optional)
            </Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Any additional details..."
              rows={2}
              className="resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-end gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isSaving || !canSubmit}
            className="bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-50"
          >
            {isSaving ? 'Pausing...' : 'Pause Task'}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  )
}
